import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApiSend } from '../../utils/httpClient';
import { login } from '../../endpoints/login';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import logo from '../../assets/PEO_InformalLogo_Color.png';
import { jwtDecode } from 'jwt-decode';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { IconButton, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Image } from 'mui-image';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  session_id_cookie?: string;
  login_cookie?: string;
  requires_password_change?: boolean;
}

interface DecodedToken {
  sub: string;
  first_name: string;
  last_name: string;
  applications: string[];
  pages: string[];
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);

  const { mutate } = useApiSend(
    login,
    (data: LoginResponse) => {
      setIsLoading(false);
      try {
        if (rememberMe) {
          localStorage.setItem('email', email);
        }
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('refresh', data.refresh_token);
        const decoded = jwtDecode<DecodedToken>(data.access_token);

        if (decoded.sub) {
          localStorage.setItem('user_id', decoded.sub);
        }
        if (decoded.first_name) {
          localStorage.setItem('first_name', decoded.first_name);
        }
        if (decoded.last_name) {
          localStorage.setItem('last_name', decoded.last_name);
        }
        if (decoded.applications?.length) {
          localStorage.setItem('apps', decoded.applications.join(','));
        }
        if (decoded.pages?.length) {
          localStorage.setItem('pages', decoded.pages.join(','));
        }

        // Get return URL: query params (from Router.jsx) are primary source of truth,
        // localStorage (from 401 error handlers) is fallback, then default to '/'
        let returnUrl =
          searchParams.get('returnUrl') ||
          localStorage.getItem('returnUrl') ||
          '/';

        // Prevent redirecting to login page (would cause infinite loop)
        // Check case-insensitively and handle query parameters
        try {
          const url = new URL(returnUrl, window.location.origin);
          const pathname = url.pathname.toLowerCase();
          if (pathname === '/login') {
            returnUrl = '/';
          }
          // Security: Ensure returnUrl is same-origin to prevent open redirect
          if (url.origin !== window.location.origin) {
            returnUrl = '/';
          }
        } catch {
          // If URL parsing fails, fall back to string check (case-insensitive)
          const pathnameMatch = returnUrl.match(/^([^?#]+)/);
          if (pathnameMatch && pathnameMatch[1].toLowerCase() === '/login') {
            returnUrl = '/';
          }
          // Security: If it's not a relative path, default to '/'
          if (
            returnUrl.startsWith('http://') ||
            returnUrl.startsWith('https://')
          ) {
            returnUrl = '/';
          }
        }

        // Check if password change is required
        if (data.requires_password_change) {
          // Store return URL for after password change (don't clear it yet)
          localStorage.setItem('returnUrl', returnUrl);
          // Verify user_id is set before navigation
          if (!localStorage.getItem('user_id')) {
            console.error('user_id not set after login, redirecting to /');
            navigate('/', { replace: true });
            return;
          }
          // Redirect to change password page
          requestAnimationFrame(() => {
            if (localStorage.getItem('user_id')) {
              navigate('/change-password', { replace: true });
            } else {
              console.error('user_id lost before navigation, redirecting to /');
              navigate('/', { replace: true });
            }
          });
          return;
        }

        // Clear returnUrl from localStorage after use (query params are ephemeral)
        localStorage.removeItem('returnUrl');

        // Verify user_id is set before navigation to prevent auth check race conditions
        if (!localStorage.getItem('user_id')) {
          console.error('user_id not set after login, redirecting to /');
          navigate('/', { replace: true });
          return;
        }

        // Use requestAnimationFrame to ensure navigation happens after React has processed
        // the localStorage update and re-rendered components. This prevents race conditions
        // where PrivateRoutes checks auth before user_id is available in the same render cycle.
        requestAnimationFrame(() => {
          // Double-check user_id is still set before navigating
          if (localStorage.getItem('user_id')) {
            navigate(returnUrl, { replace: true });
          } else {
            console.error('user_id lost before navigation, redirecting to /');
            navigate('/', { replace: true });
          }
        });
      } catch (error) {
        console.error(
          'Error decoding token or writing to localStorage:',
          error
        );
        setError('Failed to process login. Please try again.');
        setIsLoading(false);
      }
    },
    (error: Error) => {
      setError(error.message || 'An error occurred. Please try again later.');
      setIsLoading(false);
    }
  );

  const handleSubmit = async (e?: React.MouseEvent): Promise<void> => {
    setIsLoading(true);
    (mutate as any)({ email, password });
  };

  const handleRememberMe = (): void => {
    setRememberMe(!rememberMe);
    if (localStorage.getItem('email')) {
      localStorage.removeItem('email');
    }
  };
  let emailLS = localStorage.getItem('email');

  useEffect(() => {
    if (localStorage.getItem('email')) {
      setEmail(localStorage.getItem('email') || '');
      setRememberMe(true);
    }
  }, [emailLS]);

  const handleLinkPress = (e: React.MouseEvent): void => {
    e.preventDefault();
    window.open(
      'https://www.peointernational.org/about-peo/contact/',
      '_blank'
    );
  };

  const handleResetPassword = (e: React.MouseEvent): void => {
    window.location.href = '/forgot-password';
  };

  const handleKeyPress = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Grid
      container
      direction={'column'}
      alignItems="center"
      sx={{ verticalAlign: 'middle' }}
    >
      <Grid item xs={12} sx={{ pt: 2, pb: 2 }}>
        <Image
          src={logo}
          alt="Logo"
          sx={{ maxWidth: '500px' }}
          width={'40vw'}
        />
      </Grid>
      <Grid item xs={12} sx={{ pb: 2 }}>
        <Alert severity="success" sx={{ maxWidth: '500px', width: '60vw' }}>
          <AlertTitle>Welcome</AlertTitle>
          Welcome to the new P.E.O. Hub! Please note that we are continuing to
          improve the Hub and not all features are currently available. We
          appreciate your patience as we work to improve your experience.
        </Alert>
      </Grid>
      <Grid container item xs={12} justifyContent={'center'}>
        <Grid item sx={{ width: '60vw', maxWidth: '500px' }}>
          <TextField
            variant="standard"
            required
            fullWidth
            id="email"
            label="Email Address"
            autoFocus
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} justifyContent={'center'}>
        <Grid item sx={{ width: '60vw', maxWidth: '500px', mt: 2 }}>
          <TextField
            variant="standard"
            required
            fullWidth
            autoComplete="current-password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            id="password"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            onKeyDown={handleKeyPress}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid item sx={{ width: '60vw', maxWidth: '500px' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                color="primary"
                onChange={handleRememberMe}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            }
            label="Remember me"
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid item sx={{ width: '60vw', maxWidth: '500px' }}>
          <LoadingButton
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            variant="contained"
            onClick={handleSubmit}
            sx={{ mt: 1 }}
          >
            Sign In
          </LoadingButton>
          <Grid item xs textAlign={'center'}>
            {error && (
              <Typography
                variant="overline"
                color="error"
                align="center"
                fontWeight={'bold'}
              >
                {error}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            container
            xs={12}
            paddingTop={1}
            justifyContent={'space-between'}
            sx={{ width: '50vw', maxWidth: '400px' }}
          >
            <Grid item>
              <Typography
                variant="body2"
                color="primary"
                onClick={handleResetPassword}
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                Forgot Password
              </Typography>
            </Grid>
            <Grid item>
              <Typography
                variant="body2"
                color="primary"
                onClick={handleLinkPress}
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                Contact Us
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Login;
